import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import { formatDateTimeSecondsSk } from "app/utils/dateFormat";

const FieldRow = ({ label, value, mono = false }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={mono ? { fontFamily: "monospace", fontSize: "0.85rem" } : {}}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);

const getStatusColor = (status) => {
  if (!status) return "default";
  const s = String(status).toUpperCase();
  if (s === "ERROR") return "error";
  if (s === "WARNING") return "warning";
  if (s === "INFO") return "info";
  if (s === "DEBUG") return "default";
  return "default";
};

const LogDetailModal = ({ open, onClose, log: logEntry }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(null);

  if (!logEntry) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{t("pages.integrationServerLogs.modal.title")}</DialogTitle>
        <DialogContent />
        <DialogActions>
          <Button onClick={onClose}>{t("pages.integrationServerLogs.buttons.close")}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const detailsJson =
    logEntry.Details != null &&
    typeof logEntry.Details === "object" &&
    Object.keys(logEntry.Details).length > 0
      ? JSON.stringify(logEntry.Details, null, 2)
      : null;

  const fullLogLine = JSON.stringify(logEntry, null, 2);

  const handleCopy = (text, key) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {t("pages.integrationServerLogs.modal.title")}
        <IconButton onClick={onClose} size="small" aria-label={t("pages.integrationServerLogs.buttons.close")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <FieldRow
          label={t("pages.integrationServerLogs.table.logId")}
          value={logEntry.LogID}
          mono
        />
        <FieldRow
          label={t("pages.integrationServerLogs.table.createdAt")}
          value={formatDateTimeSecondsSk(logEntry.CreatedAt)}
        />
        <FieldRow
          label={t("pages.integrationServerLogs.table.status")}
          value={
            <Chip
              label={logEntry.Status}
              color={getStatusColor(logEntry.Status)}
              size="small"
            />
          }
        />
        <FieldRow
          label={t("pages.integrationServerLogs.table.entityType")}
          value={logEntry.EntityType}
        />
        <FieldRow
          label={t("pages.integrationServerLogs.table.entityId")}
          value={logEntry.EntityID}
          mono
        />
        <FieldRow
          label={t("pages.integrationServerLogs.table.action")}
          value={logEntry.Action}
        />
        <FieldRow
          label={t("pages.integrationServerLogs.table.message")}
          value={logEntry.Message}
        />

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
          {t("pages.integrationServerLogs.modal.details")}
        </Typography>
        <Box
          sx={{
            mt: 0.5,
            p: 1.5,
            bgcolor: "action.hover",
            borderRadius: 1,
            maxHeight: 320,
            overflow: "auto",
            position: "relative",
          }}
        >
          {detailsJson ? (
            <>
              <Tooltip title={copied === "details" ? t("pages.integrationServerLogs.buttons.copied") : t("pages.integrationServerLogs.buttons.copy")}>
                <IconButton
                  size="small"
                  onClick={() => handleCopy(detailsJson, "details")}
                  sx={{ position: "absolute", top: 4, right: 4 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <pre
                style={{
                  margin: 0,
                  fontFamily: "monospace",
                  fontSize: "0.8rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  paddingRight: 32,
                }}
              >
                {detailsJson}
              </pre>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("pages.integrationServerLogs.empty.noDetails")}
            </Typography>
          )}
        </Box>
        <Box sx={{ mt: 1 }}>
          <Tooltip title={copied === "full" ? t("pages.integrationServerLogs.buttons.copied") : t("pages.integrationServerLogs.buttons.copyFull")}>
            <Button
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={() => handleCopy(fullLogLine, "full")}
            >
              {copied === "full" ? t("pages.integrationServerLogs.buttons.copied") : t("pages.integrationServerLogs.buttons.copy")}
            </Button>
          </Tooltip>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("pages.integrationServerLogs.buttons.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogDetailModal;
